import { Box, Card, CardContent, Divider, Grid, Icon, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import LoadingSpinnerOverlay from "components/LoadingSpinnerOverlay/LoadingSpinnerOverlay";
import BaseLayout from "layouts/sections/components/BaseLayout";
import Gallery from "pages/Unit/HistoryCard/Gallery";
import { activityDetailAsyncApi } from "services/ApiService";
import { BaseAPIURL } from "services/AuthService";
import { useTheme } from "@emotion/react";
import ActivityDetailDownload from "./ActivityDetailDownload/ActivityDetailDownload";

// Rating display component
const RatingStars = ({ rating }) => {
  const getStars = (value) => {
    const stars = [];
    const fullStars = Math.floor(value);
    const hasHalf = value % 1 >= 0.5;

    for (let i = 0; i < fullStars; i += 1) {
      stars.push(
        <Icon key={`full-${i}`} sx={{ color: "#FFD700" }}>
          star
        </Icon>
      );
    }
    if (hasHalf) {
      stars.push(
        <Icon key="half" sx={{ color: "#FFD700" }}>
          star_half
        </Icon>
      );
    }
    while (stars.length < 5) {
      stars.push(
        <Icon key={`empty-${stars.length}`} sx={{ color: "#FFD700" }}>
          star_outline
        </Icon>
      );
    }

    return stars;
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      {getStars(rating)}
    </Box>
  );
};

const DetailItem = ({ label, value }) => (
  <Grid container spacing={1} sx={{ mb: 2 }} alignItems="center">
    <Grid item xs={4}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={8}>
      <Typography variant="body1">{value || "N/A"}</Typography>
    </Grid>
  </Grid>
);

const ActivityDetail = () => {
  const { activityId } = useParams();
  const [activity, setActivity] = useState({});
  const [reportPdfs, setReportPdfs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  const ribbonStyle = {
    backgroundColor: theme.palette.warning.main,
    color: "#fff",
    padding: "3px 25px",
    fontWeight: 600,
    fontSize: "0.75rem",
    boxShadow: theme.shadows[2],
    zIndex: 2,
    height: "100%",
    borderRadius: 4,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: theme.shadows[6],
    },
  };

  useEffect(() => {
    let subscribed = true;
    setIsLoading(true);
    activityDetailAsyncApi(activityId)
      .then((res) => {
        if (subscribed) {
          const { data } = res.data;
          setActivity(data);

          // Merge both report sources
          const reports = [...(data.reports || []), ...(data.customerProductHistories || [])];
          setReportPdfs(reports);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
      });

    return () => {
      subscribed = false;
    };
  }, [activityId]);

  return (
    <LoadingSpinnerOverlay isActive={isLoading}>
      <BaseLayout
        title="Activity Detail"
        breadcrumb={[{ label: "Activity", route: "/activity" }, { label: "Detail" }]}
      >
        <Card elevation={3}>
          <CardContent>
            <Grid container spacing={1} sx={{ mb: 2 }} justifyContent="space-between">
              <Typography variant="h5" gutterBottom>
                Activity Information
              </Typography>
              {activity.is_ongoing && <Grid sx={ribbonStyle}>Ongoing</Grid>}
            </Grid>
            <Divider sx={{ mb: 2 }} />

            <DetailItem label="Activity Code" value={activity.activity_code} />
            <DetailItem label="Date" value={activity.end_at} />
            <DetailItem label="Address" value={activity.address} />
            <DetailItem label="Description" value={activity.description} />
            <DetailItem
              label="Done By"
              value={Array.isArray(activity.activity_bies) ? activity.activity_bies.join(", ") : ""}
            />
            <DetailItem label="Submitted By" value={activity.submitted_by} />
            <DetailItem label="Signed By" value={activity.signed_by} />

            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Rating
                </Typography>
              </Grid>
              <Grid item xs={8}>
                {activity.activity_rating ? (
                  <RatingStars rating={activity.activity_rating} />
                ) : (
                  <Typography variant="body1">Rating Not Provided</Typography>
                )}
              </Grid>
            </Grid>

            <DetailItem label="Comments" value={activity.activity_comment} />
          </CardContent>
        </Card>

        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Download Report
          </Typography>
          <ActivityDetailDownload summaryPdf={activity.activity_pdf} reportPdf={reportPdfs} />
        </Box>

        {activity.photo_before?.length > 0 && (
          <Box mt={4}>
            <Gallery title="Before" data={activity.photo_before} url={BaseAPIURL} />
          </Box>
        )}
        {activity.photo_after?.length > 0 && (
          <Box mt={4}>
            <Gallery title="After" data={activity.photo_after} url={BaseAPIURL} />
          </Box>
        )}
      </BaseLayout>
    </LoadingSpinnerOverlay>
  );
};

export default ActivityDetail;
